'use client';

import { useEffect, useState } from 'react';
import { runWhenArenaIdle } from '../components/Arena/loading/sessionReplay';
import { getApiBaseUrl } from '../utils/apiBaseUrl';
import { runWhenIdle } from '../lib/browserSurface';
import { afterEngagement } from '../lib/afterEngagement';
import { readJson, stringField } from '../lib/jsonBody';

interface FacebookPixelState {
  pixelId: string | null;
  isReady: boolean;
}

const initialState: FacebookPixelState = {
  pixelId: null,
  isReady: false,
};

let cachedState: FacebookPixelState = initialState;
let initPromise: Promise<void> | null = null;
const subscribers = new Set<(s: FacebookPixelState) => void>();

function publish(next: FacebookPixelState) {
  cachedState = next;
  subscribers.forEach((cb) => cb(next));
}

function ensureInited() {
  if (initPromise) return initPromise;

  const baseUrl = getApiBaseUrl();

  initPromise = fetch(`${baseUrl}/api/public/config`)
    .then(readJson)
    .then((data) => {
      const pixelId = stringField(data, 'metaPixelId');
      if (!pixelId) {
        publish({ pixelId: null, isReady: true });
        return;
      }

      const queue: unknown[][] = [];
      const fbq: FacebookPixel =
        window.fbq ??
        Object.assign(
          function queueCall(...args: unknown[]): void {
            if (fbq.callMethod) fbq.callMethod(...args);
            else fbq.queue.push(args);
          },
          { queue, loaded: true, version: '2.0' },
        );
      fbq.push = fbq;
      window.fbq = fbq;
      window._fbq ??= fbq;

      publish({ pixelId, isReady: true });

      fbq('init', pixelId);
      fbq('track', 'PageView');

      // WHY: queued events survive engagement during combat while script insertion waits for idle outside the match.
      afterEngagement(() => {
        runWhenArenaIdle(() => {
          const script = document.createElement('script');
          script.src = 'https://connect.facebook.net/en_US/fbevents.js';
          script.async = true;
          document.head.appendChild(script);
        });
      });
    })
    .catch(() => {
      publish({ pixelId: null, isReady: true });
    });

  return initPromise;
}

export function useFacebookPixel(): FacebookPixelState {
  const [state, setState] = useState<FacebookPixelState>(cachedState);

  useEffect(() => {
    subscribers.add(setState);
    setState(cachedState);
    const cancel = runWhenIdle(
      () => {
        void ensureInited();
      },
      { timeoutMs: 3000, fallbackDelayMs: 1500 },
    );
    return () => {
      subscribers.delete(setState);
      cancel();
    };
  }, []);

  return state;
}
